import { FC, Fragment, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Types
import CaseTypes from 'app/types/CaseTypes';
// Async
import { getTeams } from 'app/store/Teams/Teams.async';
// Selectors
import { selectHcpcsCodesEnabled } from 'app/store/Accounts/Accounts.selectors';
import { selectTeamsAsOptions } from 'app/store/Teams/Teams.selectors';
// Mui
import { Box, FormHelperText, Typography, Divider, Autocomplete as MuiAutocomplete, TextField } from '@mui/material';
// Components
import { Input, DesktopDatePicker, DesktopTimePicker } from 'app/components/Mui';
import { Autocomplete } from 'app/components/Mui/Autocompletes';
// Utilieis
import { isRequired, isDateValid, isTimeValid, isMaxLength } from 'app/utilities/Validations';
// i18next
import { useTranslation } from 'react-i18next';

type Props = {
  disabled?:boolean;
  onSetError: (error:boolean) => void;
}

const CaseTabContent:FC<Props> = ({ disabled = false, onSetError }) => {
  const { t } = useTranslation();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const teamOptions = useAppSelector(selectTeamsAsOptions);
  const hcpcsCodesEnabled = useAppSelector(selectHcpcsCodesEnabled);

  const { control, formState: { errors, isSubmitted }, watch } = useFormContext();

  useEffect(() => {
    dispatch(getTeams({}));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if ( !isSubmitted ) return;
    const { claimant, claimNumber, opposingDetails, ...otherErrors } = errors as any;
    onSetError(Boolean(Object.keys(otherErrors).length));
    // eslint-disable-next-line
  }, [isSubmitted, Object.keys(errors)]);

  const watchEventDate = watch('eventDate');

  return (
    <Fragment>
      {/* Name */}
      <Controller
        control={control} name="name"
        rules={{
          required: isRequired,
          maxLength: isMaxLength(100)
        }}
        render={({ field }) => (
          <Input
            {...field}
            label={t('labels.caseName')}
            disabled={disabled}
            required
            error={Boolean(errors.name)}
            helperText={(errors.name?.message as string) || ''}
            slotProps={{
              input: {
                endAdornment: (
                  <FormHelperText
                    sx={{ p: 0, whiteSpace: 'nowrap' }}
                  >{`${watch('name').length} / 100`}</FormHelperText>
                )
              }
            }}
          />
        )}
      />
      <Controller
        control={control} name="type"
        rules={{
          maxLength: isMaxLength(100)
        }}
        render={({ field: { value, onChange } }) => (
          <MuiAutocomplete
            freeSolo
            value={value ?? ''}
            onChange={(_, value) => onChange(value)}
            onInputChange={(_, value) => onChange(value)}
            options={(Object.keys(CaseTypes) as Array<keyof typeof CaseTypes>).map(key => CaseTypes[key])}
            disabled={disabled}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('labels.type')}
                error={Boolean(errors.type)}
                helperText={(errors.type?.message as string) || ''}
              />
            )}
          />
        )}
      />
      <Box sx={{ display: 'flex', gap: 4 }} >
        <Controller
          control={control} name="eventDate"
          rules={{
            required: isRequired,
            validate: isDateValid
          }}
          render={({ field }) => (
            <DesktopDatePicker
              {...field}
              label={t('labels.eventDate')}
              disabled={disabled}
              slotProps={{
                textField: {
                  required: true,
                  error: Boolean(errors.eventDate),
                  helperText: (errors as any).eventDate?.message || ''
                }
              }}
            />
          )}
        />
        {watchEventDate ? (
          <Controller
            control={control} name="eventTime"
            rules={{
              validate: {
                pattern: isTimeValid
              }
            }}
            render={({ field }) => (
              <DesktopTimePicker
                {...field}
                label={t('labels.eventTime')}
                disabled={disabled}
                slotProps={{
                  textField: {
                    error: Boolean(errors.eventTime),
                    helperText: (errors as any).eventTime?.message || ''
                  }
                }}
              />
            )}
          />
        ) : null}
      </Box>
      <Divider sx={{ mt: 4, mb: 2 }} />
      {/* Team */}
      <Controller
        control={control} name="teamId"
        render={({ field }) => (
          <Autocomplete
            {...field}
            options={teamOptions || []}
            TextFieldProps={{
              label: t('labels.teams')
            }}
            renderOption={(props, option) => (
              <li {...props} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }} key={option.value}>
                <Typography variant="subtitle1">{option.label}</Typography>
                {option.props.description ? (
                  <Typography variant="caption">{option.props.description}</Typography>
                ) : null}
              </li>
            )}
            disabled={disabled}
          />
        )}
      />
      <FormHelperText>{t('dialogs.caseDetails.teamsHint')}</FormHelperText>
      <Box sx={{ display: 'flex', gap: 4 }}>
        {/* Examination date */}
        <Controller
          control={control} name="examinationDate"
          render={({ field }) => (
            <DesktopDatePicker
              {...field}
              label={t('labels.examinationDate')}
              disabled={disabled}
            />
          )}
        />
        {/* Trial date */}
        <Controller
          control={control} name="trialDate"
          render={({ field }) => (
            <DesktopDatePicker
              {...field}
              label={t('labels.trialDate')}
              disabled={disabled}
            />
          )}
        />
      </Box>
      <Divider sx={{ mt: 4, mb: 2 }} />

      {hcpcsCodesEnabled ? (
        <Controller
          control={control} name="settings.medicareOverchargeThreshold"
          rules={{ required: hcpcsCodesEnabled ? isRequired : false }}
          render={({ field }) => (
            <Input
              {...field}
              label={t('labels.medicareOverchargeThreshold')} type="number"
              error={Boolean((errors.settings as any)?.medicareOverchargeThreshold)}
              helperText={(errors.settings as any)?.medicareOverchargeThreshold?.message || ''}
              required={hcpcsCodesEnabled}
              disabled={disabled}
            />
          )}
        />
      ) : null}
      <FormHelperText>{t('form.helperText.medicareOverchargeThreshold')}</FormHelperText>

      <Divider sx={{ mt: 4, mb: 2 }} />

      <Controller
        control={control} name="summary"
        render={({ field }) => (
          <Input
            {...field}
            multiline
            minRows={2}
            maxRows={4}
            label={t('labels.summary')}
            disabled={disabled}
          />
        )}
      />
      <Controller
        control={control} name="expectedOutcome"
        render={({ field }) => (
          <Input
            {...field}
            multiline
            minRows={2}
            maxRows={4}
            label={t('labels.expectedOutcome')}
            disabled={disabled}
          />
        )}
      />
    </Fragment>
  )
}

export default CaseTabContent;